"use client";

import styles from "../css/expiredStock.module.css";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import SuccessNotification from "../../../../components/SuccessNotification";
import { useEffect, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { fetchExpiryStock, pullFromInventory } from "../../../../api/Inventory/ExpiryStockApi.js";
import { fetchInventoryItem } from "../../../../api/Inventory/ProductCatalogueApi.js";

// Import new components
import { getColumnsForSegment } from "../components/ColumnDefinitions";
import ExpiredStockStats from "../components/ExpiredStockStats";
import ExpiredStockFilters from "../components/ExpiredStockFilters";
import ExpiredStockModal from "../components/ExpiredStockModal";

export default function ExpiredStock() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeSegment, setActiveSegment] = useState("Front Shop");
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPullSuccess, setShowPullSuccess] = useState(false);
  const pageSize = 4;
  const filteredData = (() => {
    const query = searchQuery.trim().toLowerCase();

    // No search text: only apply segment filter and preserve original ordering
    if (!query) {
      return rows.filter((row) => !activeSegment || row.segment === activeSegment);
    }
    const nameMatches = [];
    const otherMatches = [];

    rows.forEach((row) => {
      const matchesSegment = !activeSegment || row.segment === activeSegment;
      if (!matchesSegment) return;

      const nameParts = [row.drugName, row.item?.name, row.drug?.name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const otherParts = [
        row.sku,
        row.barcode,
        row.category,
        row.suppliername,
        row.suppliercode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesName = nameParts.includes(query);
      const matchesOther = otherParts.includes(query);

      if (!matchesName && !matchesOther) return;

      if (matchesName) {
        nameMatches.push(row);
      } else {
        otherMatches.push(row);
      }
    });

    return [...nameMatches, ...otherMatches];
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchExpiryStock();
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset to first page when segment or search changes
  useEffect(() => { setCurrentPage(1); }, [activeSegment, searchQuery]);

  // If Clinic role, force Clinic segment and remove filters
  useEffect(() => {
    if (roleId === 6 && activeSegment !== "Clinic") {
      setActiveSegment("Clinic");
    }
  }, [roleId, activeSegment]);

  // Handle view more click for modal
  useEffect(() => {
    window.handleViewMoreClick = async (row) => {
      try {
        const detail = await fetchInventoryItem(row.sku);
        if (detail) {
          const merged = {
            ...row,
            sku: detail.sku ?? row.sku,
            drugName: detail.name ?? row.drugName,
            packsize: detail.packsize ?? row.packsize,
            qty: detail.quantity ?? row.qty,
            manufacturer: detail.manufacturer ?? row.manufacturer,
            costprice: detail.costprice ?? row.costprice,
            suppliercode: detail.suppliercode ?? row.suppliercode,
            suppliername: detail.suppliername ?? row.suppliername,
            lastReorder: detail.lastReorder ?? row.lastReorder,
            invoicenumber: detail.invoicenumber ?? row.invoicenumber,
            note: detail.note ?? row.note,
            drug: detail.drug ?? row.drug,
            item: detail.item ?? row.item,
          };
          setSelectedRow(merged);
        } else {
          setSelectedRow(row);
        }
      } catch {
        setSelectedRow(row);
      }
      setIsModalOpen(true);
    };

    return () => {
      delete window.handleViewMoreClick;
    };
  }, []);

  const handlePullFromInventory = async (sku) => {
    if (sku == null) return;
    try {
      await pullFromInventory(sku);
      setRows((prev) => prev.filter((row) => row.sku !== sku));
      setIsModalOpen(false);
      setSelectedRow(null);
      setShowPullSuccess(true);
    } catch (err) {
      console.error("Failed to mark inventory as out of inventory", err);
    }
  };

  const columns = getColumnsForSegment(activeSegment);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const pageData = filteredData.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [totalPages, currentPage]);

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
            <ExpiredStockStats />
            <ExpiredStockFilters
              activeSegment={activeSegment}
              onSegmentChange={setActiveSegment}
              roleId={roleId}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <Table columns={columns} data={pageData} />
            <div className={styles.paginationContainer}>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
            <ExpiredStockModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              selectedRow={selectedRow}
              activeSegment={activeSegment}
              roleId={roleId}
              onPullFromInventory={handlePullFromInventory}
            />
          </div>
        </main>
      </div>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
      {showPullSuccess && (
        <SuccessNotification
          message="Successfully pulled from inventory!"
          onClose={() => setShowPullSuccess(false)}
        />
      )}
    </div>
  );
}
